import { useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Lightbox = ({ items, index, onClose, onNavigate }) => {
  const item = index !== null ? items[index] : null;

  const prev = useCallback(() => onNavigate((index - 1 + items.length) % items.length), [index, items.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % items.length), [index, items.length, onNavigate]);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, onClose, prev, next]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={item.caption}
          data-testid="lightbox"
        >
          <button
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-red-500 hover:text-red-400"
            onClick={onClose}
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
          <button
            className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/20 text-white transition-colors hover:border-red-500 hover:text-red-400 sm:left-6"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/20 text-white transition-colors hover:border-red-500 hover:text-red-400 sm:right-6"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={20} />
          </button>
          <motion.figure
            key={(item.video || item.img) + index}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[84vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            {item.video ? (
              <video
                src={item.video}
                poster={item.img}
                controls
                autoPlay
                playsInline
                preload="none"
                className="max-h-[76vh] max-w-full"
                data-testid="lightbox-video"
              />
            ) : (
              <img src={item.img} alt={item.caption} className="max-h-[76vh] max-w-full object-contain" />
            )}
            <figcaption className="mt-4 flex items-center justify-between gap-4">
              <span className="text-sm text-zinc-300">{item.caption}</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-red-500">{item.cat}</span>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
